import { Alert, Form, Input, InputNumber, Radio, Space, Switch, Typography } from 'antd'
import { useContext, useEffect, useState } from 'react'
import { SetupContext } from '../../../pages/SetupPage/SetupContext'
import { useJobNumberingAvailable, useJobNumberingSettingsQuery, useSaveJobNumberingSettingsMutation } from '../../../domain/jobs/jobNumberingQueries'
import SettingsCard from '../../settings/SettingsCard'
import { JobNumberingFormValues, toJobNumberingFormValues, toSaveJobNumberingSettingsRequest } from './jobNumberingModel'

const JobNumbering = () => {
  const settingsQuery = useJobNumberingSettingsQuery()
  const available = useJobNumberingAvailable()
  const [saveError, setSaveError] = useState<string>()
  const saveMutation = useSaveJobNumberingSettingsMutation(setSaveError)
  const { markDirty, markClean } = useContext(SetupContext)
  const [form] = Form.useForm<JobNumberingFormValues>()
  const [isDirty, setIsDirty] = useState(false)
  const scheme = Form.useWatch('scheme', form)
  const settings = settingsQuery.data

  useEffect(() => {
    if (settings && !isDirty) {
      form.setFieldsValue(toJobNumberingFormValues(settings))
    }
  }, [settings, isDirty, form])

  if (!settingsQuery.isFeatureEnabled || (settings && !available)) {
    return <Alert type="info" showIcon message="Job numbering settings are not available for this company." />
  }

  if (!settings) {
    return null
  }

  const save = async () => {
    try {
      await form.validateFields()
    } catch {
      return
    }
    const values: JobNumberingFormValues = { ...toJobNumberingFormValues(settings), ...form.getFieldsValue(true) }
    setSaveError(undefined)
    saveMutation.mutate(toSaveJobNumberingSettingsRequest(values, settings), {
      onSuccess: () => {
        setIsDirty(false)
        markClean()
      },
    })
  }

  return <SettingsCard
    title="Job Numbering"
    description="Choose how new jobs are numbered. A job's number can still be changed on the job itself."
    onUpdate={save}
    isUpdating={saveMutation.isLoading}
    updateDisabled={!isDirty}
  >
    <Form
      form={form}
      layout="vertical"
      initialValues={toJobNumberingFormValues(settings)}
      onValuesChange={() => {
        setIsDirty(true)
        markDirty()
      }}
    >
      {saveError && <Alert type="error" showIcon style={{ marginBottom: 16 }} message={saveError} />}
      <Form.Item name="scheme" label="Numbering">
        <Radio.Group>
          <Space direction="vertical">
            <Radio value="salesperson">By salesperson: each salesperson's prefix and their own counter (for example FP-042)</Radio>
            <Radio value="company">Company sequence: one counter for the whole company</Radio>
          </Space>
        </Radio.Group>
      </Form.Item>
      {scheme === 'company' && <>
        <Form.Item name="prefix" label="Prefix" extra="Optional. Letters and numbers only.">
          <Input maxLength={settings.prefixMaxLength} style={{ width: 160 }} />
        </Form.Item>
        <Form.Item name="includeYear" label="Include the year" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="digits" label="Digits" rules={[{ required: true }]}>
          <InputNumber min={settings.minDigits} max={settings.maxDigits} precision={0} />
        </Form.Item>
        <Form.Item name="nextNumber" label="Next number" rules={[{ required: true }]}>
          <InputNumber min={1} precision={0} />
        </Form.Item>
        {settings.scheme === 'company' && settings.nextCompanyJobNumber &&
          <Typography.Text type="secondary">Next job number: <strong>{settings.nextCompanyJobNumber}</strong></Typography.Text>}
      </>}
    </Form>
  </SettingsCard>
}

export default JobNumbering
